import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Popper, Paper, Stack, Box, Grid} from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';

import {Criteria} from '../../../../../../types/Criteria';
import FocusTrap from '@mui/material/Unstable_TrapFocus';
import ArrMenu, {
  CustomMenuItem,
} from '../../../../../../../common/arr-menu/ArrMenu';
import {getOperators} from '../../../../operatorMenuUtils';
import ArrTextArea from '../../../../../../../common/arr-text-area/ArrTextArea';
import './ChipEditor.scss';
import ArrButton from '../../../../../../../common/arr-button/ArrButton';
import {t} from 'i18next';
import {useAutocomplete} from '../../../../../../../../hooks/session-search/useSessionSearch';
import {formatAutocompleteResults} from '../../../../autocompleteUtils';
import ArrAutocomplete from '../../../../../../../common/arr-autocomplete/ArrAutocomplete';
import {debounce} from 'lodash';

export interface ChipEditorProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  criteria: Criteria;
  onSave: (updated: Criteria) => void;
  onClose: () => void;
  onMenuClose: () => void;
  anchorReturnRef?: React.RefObject<HTMLElement | null>;
  restoreFocus?: boolean;
}

const ChipEditor: React.FC<ChipEditorProps> = ({
  anchorEl,
  open,
  criteria,
  onSave,
  onClose,
  onMenuClose,
  anchorReturnRef,
  restoreFocus,
}) => {
  const [field, setField] = useState('');
  const [value, setValue] = useState('');
  const popperRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<any>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const saveRef = useRef<HTMLButtonElement | null>(null);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const [autocompleteItems, setAutocompleteItems] = useState<string[]>([]);
  const [autocompleteEnabled, setAutocompleteEnabled] =
    useState<boolean>(false);

  const cleanupValue = (value: string) => {
    let cleanedValue = value;
    if (!value) return value;
    if (cleanedValue.startsWith('(')) {
      cleanedValue = cleanedValue.slice(1);
    }
    if (cleanedValue.endsWith(')')) {
      cleanedValue = cleanedValue.slice(0, -1);
    }
    return cleanedValue;
  };

  const cleanedValue = useMemo(() => cleanupValue(value), [value]);
  const autocompleteValue = useMemo(
    () => (field ? `${field}:${cleanedValue}` : cleanedValue),
    [field, cleanedValue]
  );

  const {
    data: autocompleteData,
    refetch,
    error,
  } = useAutocomplete(autocompleteValue);

  const debouncedRefetch = useMemo(
    () =>
      debounce((_: string) => {
        refetch();
      }, 300), // wait 300ms after typing
    [refetch]
  );

  useEffect(() => {
    if (open) {
      textAreaRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    // If the typed characters too short clear suggestions
    if (value.length < 3 || (value.length === 3 && value.startsWith('('))) {
      setAutocompleteItems((prev) => (prev.length ? [] : prev));
      return;
    }

    debouncedRefetch(autocompleteValue);
    return () => debouncedRefetch.cancel();
  }, [value, autocompleteValue, debouncedRefetch]);

  useEffect(() => {
    if (error) return;
    if (value.length < 3 || (value.length === 3 && value.startsWith('('))) {
      setAutocompleteItems((prev) => (prev.length ? [] : prev));
      return;
    }

    const suggestions = formatAutocompleteResults(autocompleteData ?? []);

    setAutocompleteItems((prev) =>
      prev.length === suggestions.length &&
      prev.every((x, i) => x === suggestions[i])
        ? prev
        : suggestions
    );
  }, [autocompleteData, error, autocompleteValue]);

  const handleAutocompleteSelect = (val: string) => {
    if (!val || val === value) return;
    let padded = val.trim();
    if (!padded.startsWith('(')) padded = '(' + padded;
    if (!padded.endsWith(')')) padded = padded + ')';
    setValue(padded);
  };

  const handleTyping = (val: string) => {
    setValue(val);
  };

  useEffect(() => {
    if (!criteria || !open) return;

    // Operator mode → open the menu
    if (criteria.type !== 'Operand') {
      menuRef.current?.open(anchorEl);
      return;
    }

    // Operand mode → parse the value
    const raw = criteria.value ?? '';
    const firstColon = raw.indexOf(':');

    if (firstColon === -1) {
      setField('');
      setValue(raw);
      return;
    }

    const left = raw.slice(0, firstColon).trim();
    const right = raw.slice(firstColon + 1).trim();

    if (!left) {
      setField('');
      setValue(raw);
    } else {
      setField(left);
      setValue(right);
    }
  }, [criteria, open, anchorEl]);

  const handleMenuItemClick = (item: CustomMenuItem) => {
    const updatedcriteria = {...criteria, value: item.label};
    onSave && onSave(updatedcriteria);
  };

  const handleOnSave = () => {
    const newCriteria = value.trim();

    const updated = {
      ...criteria,
      value: field ? `${field}:${newCriteria}` : newCriteria,
    };
    onSave(updated);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      handleCloseAndRestoreFocus();
      return;
    }
  };

  const handleCloseAndRestoreFocus = () => {
    onClose();

    if (!restoreFocus) return;

    requestAnimationFrame(() => {
      const el = anchorReturnRef?.current;
      if (el && document.contains(el)) {
        el.focus({preventScroll: true});
      }
    });
  };

  const renderTextComponent = () => {
    return (
      <Grid>
        <ArrAutocomplete
          value={cleanedValue}
          options={autocompleteItems}
          onChange={handleAutocompleteSelect}
          enabled={autocompleteEnabled}
          anchorEl={textAreaRef.current}
          data-testid="chip-editor-autocomplete"
        >
          <ArrTextArea
            ref={textAreaRef}
            value={value}
            rows={5}
            onChange={handleTyping}
            label={t('sessionSearch.chipEditor.value')}
            onFocus={() => setAutocompleteEnabled(true)}
            onBlur={() => setAutocompleteEnabled(false)}
            resizable={true}
            className="chipeditor-textarea"
            maxLength={4000}
            dataTestId="chip-edit-value"
            onKeyDown={(e) => {
              if (e.key === 'Tab' && !e.shiftKey) {
                e.preventDefault();
                cancelRef.current?.focus();
              }
            }}
          />
        </ArrAutocomplete>
      </Grid>
    );
  };

  return (
    <>
      {criteria.type === 'Operand' ? (
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          disablePortal={false}
          modifiers={[]}
          keepMounted
          role="dialog"
        >
          <FocusTrap open={open} disableEnforceFocus>
            <ClickAwayListener onClickAway={handleCloseAndRestoreFocus}>
              <Paper
                sx={{p: 2, width: 300}}
                className="chip-editor-popup"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                onKeyDown={handleKeyDown}
                ref={popperRef}
              >
                <Stack spacing={2}>
                  {field && (
                    <Box className="textarea-box title-textarea-box">
                      <ArrTextArea
                        dataTestId="field-edit-value"
                        value={field}
                        maxRows={4}
                        label={t('sessionSearch.chipEditor.field')}
                        rows={1}
                        minRows={1}
                        className="chipeditor-textarea"
                        onChange={(value) => setField(value)}
                        resizable={true}
                      />
                    </Box>
                  )}
                  <Box className="textarea-box value-textarea-box">
                    {renderTextComponent()}
                  </Box>

                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="space-between"
                  >
                    <ArrButton
                      size="small"
                      variant="secondary"
                      onClick={handleCloseAndRestoreFocus}
                      className="button-chip-editor-close"
                      dataTestId="button-chip-editor-close"
                      tabIndex={0}
                      ref={cancelRef}
                    >
                      {t('sessionSearch.chipEditor.cancel')}
                    </ArrButton>
                    <ArrButton
                      size="small"
                      variant="primary"
                      onClick={() => {
                        handleOnSave();
                        handleCloseAndRestoreFocus();
                      }}
                      className="button-chip-editor-save"
                      dataTestId="button-chip-editor-save"
                      tabIndex={0}
                      ref={saveRef}
                      onKeyDown={(e) => {
                        if (e.key === 'Tab' && e.shiftKey) {
                          e.preventDefault();
                          textAreaRef.current?.focus();
                        }
                      }}
                    >
                      {t('sessionSearch.chipEditor.save')}
                    </ArrButton>
                  </Stack>
                </Stack>
              </Paper>
            </ClickAwayListener>
          </FocusTrap>
        </Popper>
      ) : (
        <ArrMenu
          ref={menuRef}
          items={getOperators()}
          direction="bottom-left"
          className="chip-editor-menu"
          onItemClick={handleMenuItemClick}
          onClose={onMenuClose}
          id="operators-menu"
        />
      )}
    </>
  );
};

export default ChipEditor;
